import { useCallback, useEffect, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';

const loader = new Loader({
  apiKey: process.env.NEXT_PUBLIC_GOOGLE_KEY,
  version: 'weekly',
  libraries: ['places'],
});

/**
 * Handle google autocomplete
 * Load google map script first and then load bkash script and active script
 *
 */
const useGooglePlaceAutocomplete = (
  handlePlaceChange,
  inputId = 'google-place',
  options = {
    fields: ['formatted_address', 'geometry', 'name', 'address_components'],
    strictBounds: false,
  }
) => {
  const [place, setPlace] = useState(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [defaultPlace, setDefaultPlace] = useState({
    lat: null,
    lng: null,
    title: null,
  });

  const activeGoogleScript = useCallback(() => {
    loader.importLibrary('maps').then(async () => {
      const input = document.getElementById(inputId);
      if (input) {
        const { Autocomplete } = await google.maps.importLibrary('places');

        // Bind autocomplete to the input field
        const autocomplete = new Autocomplete(input, options);

        input.addEventListener('input', (event) => {
          if (event.target.value.length === 0) {
            if (handlePlaceChange) {
              handlePlaceChange(null);
            }
          }
        });

        autocomplete.addListener('place_changed', () => {
          const placeData = autocomplete.getPlace();

          if (!placeData.geometry || !placeData.geometry.location) {
            // User entered the name of a Place that was not suggested and
            // pressed the Enter key, or the Place Details request failed.
            window.alert(
              "No details available for input: '" + placeData.name + "'"
            );
            return;
          }

          setPlace(placeData);
          if (handlePlaceChange) {
            handlePlaceChange(placeData);
          }
        });
      }
      setScriptLoaded(true);
    });
  }, [handlePlaceChange, inputId, options]);

  const getPlaceFromLatLng = (lat, lng) => {
    if (scriptLoaded) {
      const geocoder = new google.maps.Geocoder();

      geocoder
        .geocode({ location: { lat: parseFloat(lat), lng: parseFloat(lng) } })
        .then((response) => {
          if (response.results[0]) {
            setDefaultPlace({
              lat,
              lng,
              title: response.results[0].formatted_address,
            });
          }
        })
        .catch((e) => window.alert('Geocoder failed due to: ' + e));
    }
  };

  useEffect(() => {
    activeGoogleScript();
  }, []);

  const clearData = () => {
    setPlace(null);
    const input = document.getElementById(inputId);
    input.value = '';
  };

  const setDefaultValue = (value) => {
    const input = document.getElementById(inputId);
    if (defaultPlace?.title) {
      input.value = defaultPlace?.title;
    } else {
      input.value = value;
    }
  };

  const initMap = async (position, radius, info, id) => {
    try {
      const mapOptions = {
        center: position,
        zoom: 11,
        mapId: 'a3efe1c035bad51b',
      };
      const { Map } = await google.maps.importLibrary('maps');
      // const map = new Map(document.getElementById('map'), mapOptions);
      let map;
      if (id) {
        map = new Map(document.getElementById(id), mapOptions);
      } else {
        map = new Map(document.getElementById('map'), mapOptions);
      }

      new google.maps.Circle({
        strokeColor: '#FF0000',
        strokeOpacity: 0.9,
        strokeWeight: 1,
        fillColor: '#FF0000',
        fillOpacity: 0.15,
        map,
        center: mapOptions.center,
        radius: radius * 1000,
      });

      const marker = new google.maps.Marker({
        position: mapOptions.center,
        map: map,
        icon: '/gmap-pin-icon.png',
      });
      const adressStr = [info.address.street, info.address.city]
        .filter((item) => Boolean(item))
        .join(', ');
      const infowindow = new google.maps.InfoWindow({
        content: `<div class="w-40 text-center p-1">
          <h3 class="font-bold mb-2">${info.name}</h3>
          <p>${adressStr}</p>
          </div>`,
      });

      // Open the InfoWindow immediately without waiting for a click
      infowindow.open(map, marker);
    } catch (error) {
      console.error('Error in initMap', error);
    }
  };

  // sourceData = {lat, lng, name, address}
  // destinationData = {lat, lng, name, address}
  const initTrackingMap = async (
    sourceData = {},
    destinationData = {},
    id = 'tracking-map',
    radius = 0
  ) => {
    if (sourceData?.lat && destinationData?.lat) {
      const mapOptions = {
        center: { lat: sourceData?.lat, lng: sourceData?.lng },
        zoom: 15,
        mapId: 'a3efe1c035bad51b',
      };
      const { Map } = await google.maps.importLibrary('maps');
      let map;
      if (id) {
        map = new Map(document.getElementById(id), mapOptions);
      } else {
        map = new Map(document.getElementById('map'), mapOptions);
      }

      new google.maps.Circle({
        strokeColor: '#FF0000',
        strokeOpacity: 0.9,
        strokeWeight: 1,
        fillColor: '#FF0000',
        fillOpacity: 0.15,
        map,
        center: mapOptions.center,
        radius: radius * 1000,
      });

      const sourceMarker = new google.maps.Marker({
        position: { lat: sourceData?.lat, lng: sourceData?.lng }, // Coordinates for Marker 1
        map: map,
        title: 'Source',
      });

      const sourceAddress = [
        sourceData?.address?.street_name,
        sourceData?.address.city,
      ]
        .filter((item) => Boolean(item))
        .join(', ');
      const infowindow = new google.maps.InfoWindow({
        content: `<div class="w-40 text-center p-1">
        <h3 class="font-bold mb-2">${sourceData?.name}</h3>
        <p>${sourceAddress}</p>
        </div>`,
      });

      // Open the InfoWindow immediately without waiting for a click
      infowindow.open(map, sourceMarker);

      const destinationMarker = new google.maps.Marker({
        position: {
          lat: destinationData?.lat,
          lng: destinationData?.lng,
        }, // Coordinates for Marker 2
        map: map,
        title: 'Destination',
      });

      const destinationAddress = [
        destinationData?.address?.street_name,
        destinationData?.address.city,
      ]
        .filter((item) => Boolean(item))
        .join(', ');
      const destInfowindow = new google.maps.InfoWindow({
        content: `<div class="w-40 text-center p-1">
        <h3 class="font-bold mb-2">${destinationData?.name ?? 'Customer'}</h3>
        <p>${destinationAddress}</p>
        </div>`,
      });

      // Open the InfoWindow immediately without waiting for a click
      destInfowindow.open(map, destinationMarker);

      // Create a dotted line
      const connectingLine = new google.maps.Polyline({
        path: [sourceMarker.getPosition(), destinationMarker.getPosition()],
        strokeColor: '#FF0000', // Color of the line (red in this example)
        strokeOpacity: 1.0, // Opacity
        strokeWeight: 2, // Thickness
        icons: [
          {
            icon: {
              path: 'M 0,-1 0,1',
              strokeOpacity: 1,
              scale: 4,
            },
            offset: '0',
            repeat: '20px',
          },
        ],
      });

      connectingLine.setMap(map);
    }
  };

  return {
    place,
    clearData,
    getPlaceFromLatLng,
    scriptLoaded,
    defaultPlace,
    setDefaultValue,
    initMap,
    handlePlaceChange,
    initTrackingMap,
  };
};

export default useGooglePlaceAutocomplete;
